
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Message from 'primevue/message';
import SavedCardDisplay from '@/components/billing/shared/SavedCardDisplay.vue';
import AdyenCardDropin from '@/components/billing/dialogs/AdyenCardDropin.vue';
import { brandIconFor } from '@/composables/billing/useBrandIcon';
import { ActionResult } from '@/composables/billing/billingApi';
import { PaymentMethodItem } from '@/types/billing-types';

export type PaymentMode = 'existing' | 'new';
type ViewMode = 'card' | 'select' | 'new';

/**
 * Payment block that handles three states:
 *  - `card`   : the currently selected saved card is shown (default-or-picked).
 *  - `select` : list of all valid saved cards + an "add new card" option.
 *  - `new`    : Adyen Drop-in for a brand-new card.
 *
 * The parent owns the actual payment handlers (`submitHandler` for the Drop-in
 * flow, `detailsHandler` for 3DS confirm). When the user pays with a saved
 * card, the parent reads `paymentMethodId` (v-model) and sends it to the
 * back; when they pay with a new one, the Drop-in fires `submitHandler`
 * directly without setting `paymentMethodId`.
 */
export default defineComponent({
  name: 'PaymentBlock',

  components: { Message, SavedCardDisplay, AdyenCardDropin },

  props: {
    /** Current default card from /overview (drives the initial "card" view). */
    defaultCard: {
      type: Object as PropType<PaymentMethodItem | null>,
      default: null,
    },

    /** Full list of saved cards (used to build the selection list). */
    allCards: {
      type: Array as PropType<PaymentMethodItem[]>,
      default: () => [],
    },

    /** Amount to show in the Adyen Drop-in (used by Adyen for the Pay button). */
    amount: {
      type: Number,
      required: true,
    },

    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },

    submitHandler: {
      type: Function as PropType<(stateData: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    detailsHandler: {
      type: Function as PropType<(stateData: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    /**
     * Adyen payment methods to expose in the Drop-in for the « new method »
     * flow. Forwarded as-is to {@link AdyenCardDropin.allowedMethods}.
     *   - Plan flows (ChangePlan / Migrate V5)  → ['scheme', 'paypal', 'sepadirectdebit']
     *   - One-shot flows (BuyCredits / Repay)   → ['scheme', 'paypal']
     *   - Add-a-card flow (AddPaymentMethod)    → ['scheme', 'paypal']
     */
    allowedMethods: {
      type: Array as PropType<string[]>,
      default: () => ['scheme'],
    },

    /** v-model:paymentMode — sync with parent (existing/new). */
    paymentMode: {
      type: String as PropType<PaymentMode>,
      default: 'existing',
    },

    /** v-model:paymentMethodId — id of the chosen saved card, or null = use default. */
    paymentMethodId: {
      type: String as PropType<string | null>,
      default: null,
    },

    errorMessage: {
      type: String as PropType<string | null>,
      default: null,
    },

    showEyebrow: {
      type: Boolean,
      default: true,
    },

    eyebrowLabel: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:paymentMode',
    'update:paymentMethodId',
    'success',
    'error',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();

    const hasDefaultCard = computed(() => props.defaultCard !== null);

    // Currently selected saved card id (null = use default).
    const activeCardId = ref<string | null>(
      props.paymentMethodId ?? props.defaultCard?.id ?? null,
    );

    // Resolved card object used by the "card" view header.
    const activeCard = computed<PaymentMethodItem | null>(() => {
      if (activeCardId.value) {
        const found = props.allCards.find((c) => c.id === activeCardId.value);
        if (found) return found;
      }
      return props.defaultCard;
    });

    // Valid, non-expired cards displayed in the picker. We let the user pick
    // even the current default — selecting it just confirms the existing
    // choice. Expired cards are hidden because they would refuse payment.
    const selectableCards = computed<PaymentMethodItem[]>(() => (
      props.allCards.filter((c) => !c.isExpired)
    ));

    const hasAlternatives = computed(() => (
      // Show the "Payer avec une autre carte" CTA only if there's at least
      // one card OTHER than the active one, OR if the user can add a new
      // one (always true). We prefer to always show it when there are 2+
      // saved cards or when an "add new" path is meaningful.
      selectableCards.value.length >= 1
    ));

    const viewMode = ref<ViewMode>(
      hasDefaultCard.value ? 'card' : 'new',
    );

    watch(
      () => props.paymentMode,
      (v) => {
        const target: ViewMode = v === 'new' && !hasDefaultCard.value ? 'new' : 'card';
        if (target !== viewMode.value && (v === 'existing' || !hasDefaultCard.value)) {
          viewMode.value = target;
        }
      },
    );

    // Auto-switch to `new` when no default card exists at all.
    watch(hasDefaultCard, (has) => {
      if (!has) {
        viewMode.value = 'new';
        emit('update:paymentMode', 'new');
      } else if (viewMode.value === 'new' && !props.paymentMethodId) {
        viewMode.value = 'card';
        emit('update:paymentMode', 'existing');
      }
    });

    const setView = (mode: ViewMode) => {
      viewMode.value = mode;
      emit('update:paymentMode', mode === 'new' ? 'new' : 'existing');
    };

    const onPickSaved = (card: PaymentMethodItem) => {
      activeCardId.value = card.id;
      // null when picking the default: keeps the back free to fall back to
      // `getDefaultOrOneToken`. Non-null otherwise so a specific token is used.
      emit(
        'update:paymentMethodId',
        card.id === props.defaultCard?.id ? null : card.id,
      );
      setView('card');
    };

    const onError = (err: string) => emit('error', err);

    const brandIcon = (brand: string): string => brandIconFor(brand);

    return {
      t,
      hasDefaultCard,
      hasAlternatives,
      activeCard,
      activeCardId,
      selectableCards,
      viewMode,
      setView,
      onPickSaved,
      onError,
      brandIcon,
    };
  },
});
